"use client";
import React, { useCallback } from "react";
import PropTypes from "prop-types";

// local component imports
import Registry from "../Registry";
import SectionHeader from "./sectionHeader";
import SectionFooter from "./sectionFooter";
import BackgroundRenderer from "../GenericComponents/BackgroundRenderer";

// services and utils imports
import useWidgetClickEvent, { buildDynamicHomeWidgetPayload } from "../../../../utils/dynamicHomeUtils/widgetEvent";
import { WidgetClickProvider } from "../../../../utils/dynamicHomeUtils/WidgetClickContext";
import { WIDGET_TYPE } from "../../../../constants/pageConstants/dynamicHomeConstants";

/**
 * Widget renders a single dynamic home page section with its background, header, content, and footer.
 * @param {object} props - Component props
 * @param {number} props.order - Widget render order index
 * @param {object} props.widget - Widget configuration object
 * @param {object} [props.tracking] - Feed-level CleverTap/CDP targeting (sibling of `widget` in API)
 * @param {boolean} props.isMobile - Whether the viewport is mobile
 * @param {boolean} props.isAboveFold - Whether this widget is expected above the fold
 * @param {boolean} props.isLcpCandidate - Whether images in this widget should use fetchPriority="high" (first 3 above-fold widgets)
 * @returns {React.ReactElement|null} The rendered widget or null
 */
const Widget = ({
  order,
  widget,
  isMobile,
  isAboveFold = false,
  isLcpCandidate = false,
  isMasthead = false,
  storeName,
  tracking,
}) => {
  const { triggerWidgetItemClickEvent } = useWidgetClickEvent();

  /**
   * Pre-boundynad click handler with widget-level metadata already baked in.
   * Child cells only need to supply { action, item } — no prop drilling required.
   * @param {object} params
   * @param {string} [params.action="Image Clicked"]
   * @param {object} [params.item={}]
   */
  const onCellClick = useCallback(
    ({ action = "Image Clicked", item = {} } = {}) => {
      if (!widget) return;
      triggerWidgetItemClickEvent({
        action,
        item,
        widget: buildDynamicHomeWidgetPayload(widget, order, { isMasthead, storeName, tracking }),
      });
    },
    [widget, order, isMasthead, storeName, tracking, triggerWidgetItemClickEvent],
  );

  if (!widget) return null;

  const { sectionSettings, widgetType, layoutSnapshot } = widget;

  return (
    <WidgetClickProvider value={{ onCellClick }}>
      <BackgroundRenderer
        sectionSettings={sectionSettings}
        order={order}
        widgetType={widgetType}
        gridType={layoutSnapshot?.type}
      >
        {sectionSettings?.header?.title ? (
          <SectionHeader
            sectionSettings={sectionSettings}
            isMobile={isMobile}
            headingLevel="h2"
            widgetType={widgetType}
            layoutSnapshot={layoutSnapshot}
          />
        ) : null}

        <div className="min-w-0">
          <Registry
            order={order}
            widget={widget}
            isMobile={isMobile}
            isAboveFold={isAboveFold}
            isLcpCandidate={isLcpCandidate}
          />
        </div>

        {widgetType !== WIDGET_TYPE.PRODUCTS_WITH_TABS && (
          <SectionFooter sectionSettings={sectionSettings} isMobile={isMobile} />
        )}
      </BackgroundRenderer>
    </WidgetClickProvider>
  );
};

Widget.propTypes = {
  order: PropTypes.number.isRequired,
  widget: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
  isMasthead: PropTypes.bool,
  storeName: PropTypes.string,
  tracking: PropTypes.shape({
    campaignId: PropTypes.string,
    group: PropTypes.string,
    segmentId: PropTypes.string,
  }),
};

export default Widget;
